import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlarmClock, CalendarClock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, label, toLocalInput } from "@/lib/format";
import { PageHeader, MarketSelect, PeopleSelect, SelectBox, DemoTag, Empty } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import LeadDrawer from "@/components/leads/LeadDrawer";

function Reschedule({ task, reload }) {
  const [v, setV] = useState(toLocalInput(task.due_at));
  const save = async () => {
    try {
      await api.patch(`/tasks/${task.id}`, { due_at: new Date(v).toISOString() });
      toast.success("Rescheduled");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="sm" variant="outline" className="h-8" data-testid={`reschedule-${task.id}`}><CalendarClock className="h-4 w-4" /></Button></PopoverTrigger>
      <PopoverContent className="w-64 space-y-2">
        <Input type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} data-testid={`reschedule-input-${task.id}`} />
        <Button size="sm" className="w-full bg-[#0B192C]" onClick={save} data-testid={`reschedule-save-${task.id}`}>Save</Button>
      </PopoverContent>
    </Popover>
  );
}

function Group({ title, tone, tasks, reload, onOpen, can }) {
  const done = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { done: true });
      toast.success("Follow-up completed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="mb-7" data-testid={`task-group-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${tone}`}>{title}<span className="num text-xs bg-white border rounded px-1.5">{tasks.length}</span></h3>
      {tasks.length === 0 ? <Empty>Nothing here.</Empty> : (
        <div className="bg-white border rounded-md divide-y">
          {tasks.map((t) => (
            <div key={t.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3" data-testid={`task-row-${t.id}`}>
              <button className="text-left min-w-0" onClick={() => onOpen(t.lead_id)}>
                <div className={`text-sm font-medium ${t.done ? "line-through text-slate-400" : ""}`}>{t.title}<DemoTag show={t.is_demo} /></div>
                <div className="text-xs text-slate-500">{t.lead_name}{t.lead_company ? ` · ${t.lead_company}` : ""} · {label(t.type)} · {t.assigned_name || "Unassigned"}</div>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs flex items-center gap-1 ${t.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`}><AlarmClock className="h-3 w-3" />{dateTime(t.due_at)}</span>
                {!t.done && can("tasks.manage") && (
                  <>
                    <Reschedule task={t} reload={reload} />
                    <Button size="sm" variant="outline" className="h-8" onClick={() => done(t)} data-testid={`task-complete-${t.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Done</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Tasks() {
  const { can } = useAuth();
  const [market, setMarket] = useState("");
  const [assigned, setAssigned] = useState("");
  const [status, setStatus] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks", { params: { status, market: market || undefined, assigned_to: assigned || undefined } });
      setTasks(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [status, market, assigned]);
  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
  const nowIso = now.toISOString();
  const open = tasks.filter((t) => !t.done);
  const props = { reload: load, onOpen: setOpenId, can };

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow="Follow-through" title="Tasks & Follow-ups" subtitle="Calls, visits and follow-ups across your visible leads. Overdue items are flagged in red.">
        <SelectBox value={status} onChange={(v) => setStatus(v || "open")} testid="task-status-filter" options={[{ value: "open", label: "Open" }, { value: "done", label: "Completed" }]} />
        <MarketSelect value={market} onChange={setMarket} />
        {can("leads.view_all") && <PeopleSelect value={assigned} onChange={setAssigned} />}
      </PageHeader>
      {status === "open" ? (
        <>
          <Group title="Overdue" tone="text-red-600" tasks={open.filter((t) => t.due_at < nowIso)} {...props} />
          <Group title="Today" tone="text-orange-600" tasks={open.filter((t) => t.due_at >= nowIso && t.due_at <= endToday)} {...props} />
          <Group title="Upcoming" tone="text-[#0B192C]" tasks={open.filter((t) => t.due_at > endToday)} {...props} />
        </>
      ) : <Group title="Completed" tone="text-emerald-700" tasks={tasks} {...props} />}
      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
